import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { clinicInfo } from '../mockData';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Show success message
    toast.success('Appointment request submitted successfully! We will contact you shortly.');

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      date: '',
      time: '',
      service: '',
      message: ''
    });

    setIsSubmitting(false);
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-cyan-600 via-sky-500 to-blue-600 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            {...fadeInUp}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Book your appointment or reach out with any questions you may have
            </p>
          </motion.div>
        </div>
      </section>

      <br/>

      {/* Contact Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
      {[
        { Icon: Phone, title: 'Phone', content: <a href={`tel:${clinicInfo.phone}`} className="text-cyan-600 hover:text-cyan-700">{clinicInfo.phone}</a> },
        { Icon: Mail, title: 'Email', content: <a href={`mailto:${clinicInfo.email}`} className="text-cyan-600 hover:text-cyan-700">{clinicInfo.email}</a> },
        { Icon: MapPin, title: 'Address', content: <p className="text-gray-600">{clinicInfo.address}</p> },
        {
          Icon: Clock,
          title: 'Working Hours',
          content: (
            <div className="space-y-1 text-sm text-gray-600">
              <p>{clinicInfo.workingHours.weekdays}</p>
              <p>{clinicInfo.workingHours.saturday}</p>
              <p className="text-red-500 font-semibold">{clinicInfo.workingHours.sunday}</p>
            </div>
          )
        }
      ].map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: i * 0.1 }}
          className="flex items-start space-x-4 p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <div className="bg-gradient-to-br from-cyan-500 to-blue-500 p-3 rounded-xl">
            <item.Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
            {item.content}
          </div>
        </motion.div>
      ))}
    </div>

    <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
    className="w-full h-screen"
  >
    <iframe
      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1918.7408068111938!2d74.50966989999999!3d15.883820000000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bbf61f7d1972f1b%3A0x6e57ede0923cfeab!2sV%20Derma%20Skin%20Clinic!5e0!3m2!1sen!2sin!4v1766645575409!5m2!1sen!2sin"
      width="100%"
      height="100%"
      style={{ border: 0 }}
      allowFullScreen=""
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      title="Clinic Location"
    />
  </motion.div>
    </div>
  );
};

export default Contact;
